import React, { useState, useEffect, useCallback, memo } from "react";
import { Card, Select, DatePicker, Button, Table, message } from "antd";
import moment from "moment";
import { getAllStates, getAuditRegister } from "./laundryAuditApi";

const { Option } = Select;

const pctColor = (pct) => {
  if (pct >= 80) return "#2E7D4F";
  if (pct >= 50) return "#A66423";
  if (pct > 0) return "#A63636";
  return undefined;
};

// Unlike Cleaning Audit's Audit Register, this backend already returns fully
// shaped data — { facilities: [{ facility_id, facility_name, monthly_data:
// [{month_number, month_name, completion_percentage}, ...12] }] } — confirmed
// by reading MajesticERP-LaundryAuditAPI/code/controllers/AuditReportsController.js's
// getAuditRegister handler directly. No client-side grouping needed.
const AuditRegister = () => {
  const [states, setStates] = useState([]);
  const [stateId, setStateId] = useState(null);
  const [year, setYear] = useState(moment());
  const [loading, setLoading] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    (async () => {
      const { error, result } = await getAllStates();
      setStates(!error && Array.isArray(result?.result) ? result.result : []);
    })();
  }, []);

  const search = useCallback(async () => {
    if (!stateId) {
      message.error("Please select a state first.");
      return;
    }
    setLoading(true);
    setSearched(true);
    const { error, result } = await getAuditRegister({ year: year.format("YYYY"), state_id: stateId });
    setFacilities(!error && Array.isArray(result?.result?.facilities) ? result.result.facilities : []);
    setLoading(false);
  }, [stateId, year]);

  const columns = facilities[0]
    ? [
        { title: "Facility Name", dataIndex: "facility_name", key: "facility_name", fixed: "left" },
        ...facilities[0].monthly_data.map((m, i) => ({
          title: m.month_name,
          key: `m${i}`,
          render: (_, row) => {
            const cell = row.monthly_data[i];
            if (!cell || cell.total_audits === 0) return "-";
            return <span style={{ color: pctColor(cell.completion_percentage), fontWeight: 600 }}>{cell.completion_percentage}%</span>;
          },
        })),
      ]
    : [{ title: "Facility Name", dataIndex: "facility_name", key: "facility_name" }];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Year</div>
            <DatePicker
              picker="year"
              value={year}
              onChange={(d) => d && setYear(d)}
              allowClear={false}
              disabledDate={(d) => d && d > moment().endOf("year")}
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>State</div>
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Select a state"
              style={{ width: 220 }}
              value={stateId}
              onChange={setStateId}
            >
              {states.map((s) => <Option key={s.state_id} value={s.state_id}>{s.state_name}</Option>)}
            </Select>
          </div>
          <Button type="primary" loading={loading} onClick={search}>Search</Button>
        </div>
      </Card>

      {searched && (
        <Card title="Audit Completion Report">
          <Table
            rowKey="facility_id"
            columns={columns}
            dataSource={facilities}
            loading={loading}
            scroll={{ x: true }}
            pagination={{ pageSize: 20 }}
            locale={{ emptyText: "No records for this year/state." }}
          />
        </Card>
      )}
    </div>
  );
};

export default memo(AuditRegister);
